import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  BrainCircuit,
  Network,
  ShieldCheck,
  Gauge,
  Terminal,
} from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { SPECIALTY, AI_FLOW, AI_USE_CASES } from "@/data/site";
import {
  Seo,
  serviceJsonLd,
  breadcrumbJsonLd,
} from "@/components/site/Seo";

const PILLARS = [
  {
    icon: Network,
    title: "Spezialisierte Agenten statt einer KI",
    desc: "Eigenständige Agenten mit klaren Aufgaben, organisiert in Fachabteilungen, verbunden durch gemeinsames Unternehmenswissen.",
  },
  {
    icon: BrainCircuit,
    title: "MAGI-Entscheidungssystem",
    desc: "Bewertet Fragestellungen mehrstufig und aus verschiedenen Blickwinkeln, transparent begründet statt Black-Box.",
  },
  {
    icon: ShieldCheck,
    title: "Mensch behält die Kontrolle",
    desc: "Freigaben für kritische Aktionen, einsehbare Entscheidungen und ein zentraler Notstopp auf Knopfdruck.",
  },
  {
    icon: Gauge,
    title: "Echtzeit-Transparenz & Kosten",
    desc: "Sichtbar, welche Agenten aktiv sind, welche Prozesse laufen und welche KI-Kosten entstehen.",
  },
];

const FeaturedUseCase = ({ uc }) => (
  <motion.div
    variants={fadeUp}
    data-testid="ai-usecase-featured"
    className="lx-terminal overflow-hidden p-7 sm:p-9"
    style={{
      background: "linear-gradient(140deg, rgba(168,232,92,0.08), rgba(14,20,16,0.92) 60%)",
      borderColor: "rgba(168,232,92,0.3)",
    }}
  >
    <div className="flex items-center gap-3">
      <span
        className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px]"
        style={{ background: "rgba(168,232,92,0.14)", color: "var(--lx-green)" }}
      >
        <uc.icon size={24} />
      </span>
      <span
        className="rounded-full border px-3 py-1 font-mono-lx text-[11px] uppercase tracking-[0.16em]"
        style={{ borderColor: "rgba(168,232,92,0.4)", color: "var(--lx-green-soft)" }}
      >
        Use Case · {uc.category}
      </span>
    </div>

    <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
      {uc.title}
    </h3>

    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <div className="lx-kicker mb-2">Ausgangslage</div>
        <p className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{uc.situation}</p>
      </div>
      <div>
        <div className="lx-kicker mb-2">Die Lösung</div>
        <p className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{uc.solution}</p>
      </div>
      <div>
        <div className="lx-kicker mb-2">MAGI & Superhirn</div>
        <p className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{uc.magi}</p>
      </div>
      <div>
        <div className="lx-kicker mb-2">Mensch in Kontrolle</div>
        <p className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{uc.control}</p>
      </div>
    </div>

    {/* Ergebnis */}
    <div className="mt-8">
      <div className="lx-kicker mb-3">Das Ergebnis</div>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {uc.results.map((r) => (
          <li key={r} className="flex items-start gap-3">
            <span
              className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-[8px]"
              style={{ background: "rgba(168,232,92,0.12)", color: "var(--lx-green)" }}
            >
              <Check size={14} />
            </span>
            <span className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{r}</span>
          </li>
        ))}
      </ul>
    </div>

    {uc.reference && (
      <div className="mt-8 flex flex-wrap items-center gap-4 border-t pt-6" style={{ borderColor: "var(--lx-border)" }}>
        <Link
          to={uc.reference.to}
          data-testid="ai-usecase-reference"
          className="group inline-flex items-center gap-2 rounded-[12px] px-4 py-2.5 font-display text-sm font-medium"
          style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
        >
          {uc.reference.name} ansehen
          <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    )}
  </motion.div>
);

export default function AiAgentsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const featured = AI_USE_CASES.find((u) => u.featured);
  const teasers = AI_USE_CASES.filter((u) => !u.featured);

  return (
    <div className="lx-noise relative overflow-hidden">
      <Seo
        path="/ki-agenten"
        title="KI-Agenten erstellen – autonome KI-Agenten & Automatisierung"
        description="KI-Agenten erstellen lassen aus der Schweiz: autonome, spezialisierte KI-Agenten für Sales, Support, Marketing & Backoffice. Multi-Agenten-Systeme mit MAGI-Entscheidungslogik, Freigaben und Notstopp."
        jsonLd={[
          serviceJsonLd({
            name: "KI-Agenten & Automatisierung",
            description:
              "Entwicklung autonomer KI-Agenten und Multi-Agenten-Systeme für Vertrieb, Support, Marketing und Backoffice, mit transparenter Entscheidungslogik und menschlicher Kontrolle.",
            path: "/ki-agenten",
          }),
          breadcrumbJsonLd([
            { name: "Start", path: "/" },
            { name: "KI-Agenten", path: "/ki-agenten" },
          ]),
        ]}
      />

      <div className="lx-hero-ambient absolute inset-0 -z-10" />
      <div className="mx-auto max-w-6xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-8 lg:pb-28 lg:pt-40">
        <Link
          to="/"
          data-testid="ai-back-link"
          className="lx-navlink inline-flex items-center gap-2 font-mono-lx text-xs uppercase tracking-[0.18em]"
        >
          <ArrowLeft size={14} /> Zurück zur Startseite
        </Link>

        {/* Hero */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="mt-8 max-w-3xl">
          <motion.div
            variants={fadeUp}
            className="mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5"
            style={{ borderColor: "rgba(168,232,92,0.22)", background: "rgba(168,232,92,0.07)" }}
          >
            <Terminal size={13} style={{ color: "var(--lx-green)" }} />
            <span className="font-mono-lx text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--lx-green-soft)" }}>
              {SPECIALTY.badge}
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            data-testid="ai-title"
            className="font-display text-4xl font-semibold leading-[1.06] tracking-tight text-[color:var(--lx-text)] sm:text-5xl lg:text-6xl"
          >
            KI-Agenten, die mitdenken,{" "}
            <span style={{ color: "var(--lx-green)" }}>entscheiden</span> und handeln.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-base leading-relaxed text-[color:var(--lx-text-2)] sm:text-lg"
          >
            {SPECIALTY.desc}
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/#kontakt"
              data-testid="ai-primary-cta"
              className="group inline-flex h-12 items-center gap-2 rounded-[12px] px-6 font-display font-medium"
              style={{ background: "var(--lx-green)", color: "var(--lx-canvas)", boxShadow: "0 10px 30px rgba(168,232,92,0.16)" }}
            >
              KI-Agenten anfragen
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/projekte/liberox-genesis"
              data-testid="ai-secondary-cta"
              className="inline-flex h-12 items-center gap-2 rounded-[12px] border px-6 font-display font-medium"
              style={{ borderColor: "var(--lx-border-strong)", background: "transparent", color: "var(--lx-text)" }}
            >
              Liberox Genesis ansehen
            </Link>
          </motion.div>
        </motion.div>

        {/* Pillars */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {PILLARS.map((p) => (
            <motion.div key={p.title} variants={fadeUp} className="lx-card p-6" data-testid="ai-pillar">
              <span
                className="mb-4 grid h-11 w-11 place-items-center rounded-[12px] border"
                style={{ borderColor: "var(--lx-border-strong)", background: "var(--lx-surface-2)", color: "var(--lx-green)" }}
              >
                <p.icon size={20} />
              </span>
              <h3 className="font-display text-base font-semibold text-[color:var(--lx-text)]">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--lx-text-2)]">{p.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Flow / Ablauf */}
        <section className="mt-20">
          <div className="lx-kicker mb-3">Der Ablauf, vollautomatisch</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
            Erkennen → bewerten → ansprechen → nachfassen → übergeben.
          </h2>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5"
          >
            {AI_FLOW.map((step, i) => (
              <motion.div key={step.title} variants={fadeUp} className="lx-card p-5" data-testid="ai-flow-step">
                <div className="font-mono-lx text-sm" style={{ color: "var(--lx-green)" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-2 font-display text-[15px] font-semibold text-[color:var(--lx-text)]">
                  {step.title}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-[color:var(--lx-text-2)]">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Featured use case */}
        {featured && (
          <section className="mt-20">
            <div className="lx-kicker mb-3">Use Case im Detail</div>
            <h2 className="mb-8 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
              So übernimmt KI echte Verantwortung.
            </h2>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
              <FeaturedUseCase uc={featured} />
            </motion.div>
          </section>
        )}

        {/* Teaser use cases */}
        {teasers.length > 0 && (
          <section className="mt-16">
            <div className="lx-kicker mb-3">Weitere Einsatzgebiete</div>
            <h2 className="mb-8 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
              KI-Agenten über alle Abteilungen.
            </h2>
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="grid grid-cols-1 gap-5 sm:grid-cols-3"
            >
              {teasers.map((uc) => (
                <motion.div key={uc.id} variants={fadeUp} className="lx-card p-6" data-testid="ai-usecase-teaser">
                  <span
                    className="mb-4 grid h-11 w-11 place-items-center rounded-[12px] border"
                    style={{ borderColor: "var(--lx-border-strong)", background: "var(--lx-surface-2)", color: "var(--lx-green)" }}
                  >
                    <uc.icon size={20} />
                  </span>
                  <div className="font-mono-lx text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--lx-muted)" }}>
                    {uc.category}
                  </div>
                  <h3 className="mt-1.5 font-display text-base font-semibold text-[color:var(--lx-text)]">
                    {uc.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[color:var(--lx-text-2)]">{uc.summary}</p>
                </motion.div>
              ))}
            </motion.div>
          </section>
        )}

        {/* CTA */}
        <section className="mt-20">
          <div
            className="lx-terminal p-8 text-center sm:p-12"
            style={{
              background: "linear-gradient(180deg, rgba(168,232,92,0.07), rgba(14,20,16,0.9))",
              borderColor: "rgba(168,232,92,0.28)",
            }}
          >
            <BrainCircuit size={26} className="mx-auto" style={{ color: "var(--lx-green)" }} />
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
              Lass uns deinen KI-Agenten bauen.
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-[color:var(--lx-text-2)] sm:text-base">
              Ob einzelner Assistent oder vernetztes Agenten-System: Wir zeigen dir, wo KI in deinem Betrieb echte Verantwortung übernehmen kann.
            </p>
            <Link
              to="/#kontakt"
              className="group mt-6 inline-flex items-center gap-2 rounded-[12px] px-6 py-3 font-display font-medium"
              style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
            >
              Projekt anfragen
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
